import React, { useState, useCallback } from 'react';
import { FileUpload } from './FileUpload';
import { useEntityFileAttachments, useDeleteFileAttachment } from '../../hooks/useFileAttachments';
import { normalizeFileName } from './utils/fileNameEncoding';

const FormFileUpload = React.forwardRef(({ 
  name, 
  label, 
  value = [], 
  onChange, 
  entityTypeId, 
  entityId, 
  multiple = true,
  accept,
  maxSize,
  maxFiles,
  placeholder,
  required = false,
  disabled = false,
  className = '',
  onFilesToDeleteChange
}, ref) => {
  const [isUploading, setIsUploading] = useState(false);
  const [filesToDelete, setFilesToDelete] = useState([]);
  
  // Fetch existing files for this entity
  const { data: existingFiles = [], isLoading: loadingExistingFiles, mutate: mutateExistingFiles } = useEntityFileAttachments(entityTypeId, entityId);
  
  // Clear filesToDelete when entityId changes (e.g., when switching to a different record)
  React.useEffect(() => {
    if (entityId) {
      setFilesToDelete([]);
    }
  }, [entityId]);
  
  // Delete file attachment hook
  const { deleteFileAttachment, isDeleting } = useDeleteFileAttachment();

  const handleFiles = useCallback((eventOrFiles) => {
    let files = [];
    
    // Handle both direct FileList and event objects
    if (eventOrFiles.target && eventOrFiles.target.files) {
      files = Array.from(eventOrFiles.target.files);
    } else if (eventOrFiles instanceof FileList) {
      files = Array.from(eventOrFiles);
    } else if (Array.isArray(eventOrFiles)) {
      files = eventOrFiles;
    }

    // Create file attachment objects with the actual File objects
    const fileAttachments = files.map(file => ({
      file: file, // Keep the actual File object for upload
      fileName: normalizeFileName(file.name),
      fileType: file.type,
      fileSize: file.size,
      entityTypeId: entityTypeId,
      entityId: entityId ? Number(entityId) : null,
      description: '',
      isPrivate: false,
      uploadedAt: new Date().toISOString()
    }));

    console.log('📎 FileUpload: Files selected:', fileAttachments.length, 'files');
    
    // Call onChange with the new file attachments (don't combine with existing files)
    if (onChange) {
      onChange({
        target: {
          name,
          value: fileAttachments,
          fileAttachments: fileAttachments
        }
      });
    }
  }, [name, onChange, entityTypeId, entityId, existingFiles]);

  // For display, show existing files and newly selected files separately
  // Filter out any files from value that are already in existingFiles to avoid duplication
  const newFilesOnly = Array.isArray(value) ? value.filter(newFile => 
    !existingFiles.some(existingFile => 
      existingFile.id === newFile.id || 
      existingFile.fileName === newFile.fileName
    )
  ) : [];
  
  // Filter out files that are marked for deletion or have isDeleted=true
  const filteredExistingFiles = existingFiles.filter(file => 
    !file.isDeleted && // Exclude files that are marked as deleted in the database
    !filesToDelete.some(fileToDelete => fileToDelete.id === file.id) // Exclude files marked for deletion in UI
  );
  
  const displayFiles = filteredExistingFiles.map(file => ({
    ...file,
    isExisting: true // Mark existing files
  })).concat(newFilesOnly.map(file => ({
    ...file,
    isExisting: false // Mark newly selected files
  })));

  const handleRemoveFile = useCallback((index) => {
    // Check if we're removing an existing file or a newly selected file
    const fileToRemove = displayFiles[index];
    
    if (fileToRemove.isExisting) {
      // For existing files, mark them for deletion (soft delete)
      const newFilesToDelete = [...filesToDelete, fileToRemove];
      setFilesToDelete(newFilesToDelete);
      console.log('File marked for deletion:', fileToRemove.fileName);
      
      // Notify parent component about files marked for deletion
      if (onFilesToDeleteChange) {
        onFilesToDeleteChange(newFilesToDelete);
      }
    } else {
      // For newly selected files, we need to find the correct index in the newFilesOnly array
      const existingFilesCount = filteredExistingFiles.length;
      const actualIndex = index - existingFilesCount;
      
      if (actualIndex >= 0 && actualIndex < newFilesOnly.length) {
        // Remove the file from newFilesOnly and update the value
        const fileToRemoveFromNew = newFilesOnly[actualIndex];
        
        // Since newFilesOnly is derived from value (filtered to exclude existing files),
        // we need to find the corresponding file in value and remove it
        // Use File object reference as the most reliable identifier
        const newFiles = Array.isArray(value) ? value.filter((file) => {
          // Match by File object reference (most reliable - each File object is unique)
          if (file.file && fileToRemoveFromNew.file) {
            return file.file !== fileToRemoveFromNew.file; // Keep if NOT the same File object
          }
          
          // Fallback: if File objects aren't available, use a unique combination
          // Match by fileName, fileSize, and lastModified to uniquely identify the file
          const fileLastModified = file.file?.lastModified || file.lastModified || 0;
          const removeLastModified = fileToRemoveFromNew.file?.lastModified || fileToRemoveFromNew.lastModified || 0;
          
          // If all three match, it's the same file - remove it
          if (file.fileName === fileToRemoveFromNew.fileName &&
              file.fileSize === fileToRemoveFromNew.fileSize &&
              fileLastModified === removeLastModified) {
            return false; // Remove this file
          }
          
          return true; // Keep this file - it doesn't match
        }) : [];
        
        console.log('Removing new file:', fileToRemoveFromNew.fileName, 'from', value.length, 'files, remaining:', newFiles.length);
        
        if (onChange) {
          onChange({
            target: {
              name,
              value: newFiles,
              fileAttachments: newFiles
            }
          });
        }
      }
    }
  }, [name, onChange, value, displayFiles, filteredExistingFiles.length, newFilesOnly, filesToDelete, onFilesToDeleteChange]);

  // Function to get files marked for deletion (for parent component)
  const getFilesToDelete = useCallback(() => {
    return filesToDelete;
  }, [filesToDelete]);

  // Expose the getFilesToDelete function via ref or callback
  React.useImperativeHandle(ref, () => ({
    getFilesToDelete
  }), [getFilesToDelete]);

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
             <FileUpload
         name={name}
         multiple={multiple}
         accept={accept}
         maxSize={maxSize}
         maxFiles={maxFiles}
         placeholder={placeholder}
         disabled={disabled || isUploading || isDeleting}
         onFilesChange={handleFiles}
         files={displayFiles}
         onRemoveFile={handleRemoveFile}
         showExistingFiles={true}
         existingFiles={filteredExistingFiles}
         loadingExistingFiles={loadingExistingFiles}
       />
      
             {(isUploading || isDeleting) && (
         <div className="text-sm text-blue-600 dark:text-blue-400">
           {isUploading ? 'Uploading files...' : 'Deleting file...'}
         </div>
       )}
    </div>
  );
});

FormFileUpload.displayName = "FormFileUpload";

export default FormFileUpload;
